import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  AutocompleteCloseReason,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  Popper,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import * as React from 'react';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const PopperStyledComponent = styled(Popper)(({ theme }) => ({
  border: `1px solid ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  popover: {
    pointerEvents: 'none',
  },
}));

/* 
  Component design inspired by:
  https://github.com/mui/material-ui/issues/21211#issuecomment-1024542465
  https://github.com/mui/material-ui/issues/19137#issuecomment-844909627 
*/

interface MultiSelectProps<T> {
  label: string;
  options: T[];
  placeholder: string;
  value: T[];

  getOptionLabel: (option: T) => string;
  onChange: (value: T[]) => void; // Use values from onChange for retrieving every selected option as selection happens
  onAccept?: (value: T[]) => void; // Use the values from onAccept if you want to only get values from when the user accepts their selections
}

function MultiSelect<T>({
  label,
  options,
  placeholder,
  value,
  getOptionLabel,
  onChange,
  onAccept,
}: MultiSelectProps<T>): React.ReactElement {
  const [checkAll, setCheckAll] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [applyPerformed, setApplyPerformed] = React.useState(false);
  const [tempValues, setTempValues] = React.useState<T[]>(value); // Used to reset values when closing popper without applying changes

  const checkAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckAll(event.target.checked);
    if (event.target.checked) {
      onChange(options);
    } else {
      onChange([]);
    }
  };

  const handleClickAway = (e: MouseEvent | TouchEvent) => {
    if (!applyPerformed && open) {
      onChange(tempValues);
    }
    setApplyPerformed(false);
    setOpen(false);
  };

  const handleApply = () => {
    if (onAccept) {
      setApplyPerformed(true);
      onAccept(value);
      setOpen(false);
    }
  };

  const handleOpen = () => {
    setTempValues(value); // Stores values representing state of when popper was opened
    setOpen(true);
  };

  const handleClose = (
    event: React.SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason
  ) => {
    if (reason === 'escape') {
      setOpen(false);
    }
  };

  const sortOptionsBySelectedValues = (): T[] => {
    const selectedOptions = options.filter((option) => value.includes(option));
    const unselectedOptions = options.filter((option) => !value.includes(option));
    return [...selectedOptions, ...unselectedOptions];
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={3}
          options={sortOptionsBySelectedValues()}
          value={value}
          open={open}
          onChange={(event, newValue, reason) => {
            if (reason === 'selectOption') {
              onChange(newValue);
            } else if (reason === 'removeOption') {
              setCheckAll(false);
              onChange(newValue);
            } else if (reason === 'clear') {
              onChange([]);
              setCheckAll(false);
            }
          }}
          onClose={handleClose}
          onOpen={handleOpen}
          PopperComponent={(param) => (
            <PopperStyledComponent {...param}>
              <Box {...param} />
              <Divider />
              <Box
                sx={{
                  backgroundColor: 'white',
                  height: '45px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <Checkbox
                  checked={checkAll}
                  onChange={checkAllChange}
                  id="check-all"
                  sx={{ ml: '12.5px', mr: '10px' }}
                />
                Select All
              </Box>
              <Box
                sx={{
                  p: 1,
                  backgroundColor: 'white',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {onAccept && (
                  <Button variant="contained" fullWidth onClick={handleApply}>
                    Apply
                  </Button>
                )}
              </Box>
            </PopperStyledComponent>
          )}
          getOptionLabel={getOptionLabel}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected || checkAll}
              />
              {getOptionLabel(option)}
            </li>
          )}
          style={{ width: '100%' }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              sx={{ display: 'block' }}
              onFocus={() => setOpen(true)}
            />
          )}
          renderTags={(value, getTagProps) => {
            const numTags = value.length;

            return (
              // Hack to make tags clickable
              <Box sx={{ width: '100%' }} onClick={() => setOpen(true)}>
                <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                  {value
                    .slice(0, 2)
                    .map((option, _) => getOptionLabel(option))
                    .join(', \n')}

                  {numTags > 2 && ` +${numTags - 2} more`}
                </Typography>
              </Box>
            );
          }}
        />
      </Box>
    </ClickAwayListener>
  );
}

MultiSelect.defaultProps = {
  label: '',
  placeholder: '',
};

export default MultiSelect;
